import { useCallback, useEffect } from 'react';
import { fabric } from 'fabric';
import { FabricObjectWithSrc, getImageNameWithoutExtensions, sortImagesAscFunction } from '../../../../shared/Helpers';
import { NewFindingStatus, SequenceInfo } from '../../Annotation/annotation.types';
import { AnnotatedFinding } from '../../Canvas/prepareShapesSelector';
import {
  fabricObjectProperties,
  lockedFabricObjectProperties,
  indicatorTransparentColor,
  shouldShowFinding,
  strokeWidthThick
} from '../../Canvas/Helpers';
import { getTopLeftOfImageBoundingBox } from './Helpers';

function useSetImages(
  canvasEl: Nullable<fabric.Canvas>,
  fabricImages: FabricObjectWithSrc[],
  onImagesLoaded: Function,
  sequenceInfo: SequenceInfo | null,
  subsampleFactor: number,
  imagesLoaded: boolean,
  startImagesLoading: () => void,
  toggleAllUserFindingsVisibility: boolean,
  toggleMLFindingsVisibility: boolean
) {
  const loadFindings = useCallback(
    (shapes: AnnotatedFinding[], image: FabricObjectWithSrc, fabricImage: fabric.Image) => {
      const ratio = 1 / subsampleFactor;
      if (shapes && ratio) {
        shapes.forEach(shapeBox => {
          if (!shouldShowFinding(toggleAllUserFindingsVisibility, toggleMLFindingsVisibility, shapeBox.isReviewed || shapeBox.isQaReviewed)) {
            return;
          }

          const shape = shapeBox.homographyShape ? shapeBox.homographyShape : shapeBox.shape;
          const [xmin, ymin] = getTopLeftOfImageBoundingBox(fabricImage);
          let newShape = shape.map(x => ({
            x: x.x * ratio + xmin!,
            y: x.y * ratio + ymin!
          }));

          const polygon = new fabric.Polygon(newShape, {
            name: shapeBox.id,
            ...fabricObjectProperties,
            ...lockedFabricObjectProperties,
            fill: indicatorTransparentColor,
            stroke: 'transparent'
          });

          canvasEl!.add(polygon);
          canvasEl!.renderAll();

          const allObjects = canvasEl!.getObjects('polygon');
          allObjects.forEach(x => canvasEl!.bringToFront(x));
        });
      }
    },
    [canvasEl, subsampleFactor, toggleAllUserFindingsVisibility, toggleMLFindingsVisibility]
  );

  const setImages = useCallback(() => {
    if (!canvasEl || imagesLoaded) return;

    if (fabricImages.length === 0) {
      onImagesLoaded(false);
      return;
    }

    startImagesLoading();

    canvasEl.clear();

    let imagesCount = 0;
    fabricImages.forEach(fabricJsImage => {
      var img = new Image();
      img.src = fabricJsImage.src;
      img.onload = event => {
        var fabricImage = new fabric.Image(img, fabricJsImage);

        canvasEl!.add(fabricImage);

        imagesCount++;

        if (imagesCount === fabricImages.length) {
          // sort
          const objects = canvasEl.getObjects();
          const sortedImages = [...fabricImages].sort(sortImagesAscFunction);
          sortedImages.forEach(image => {
            let object = objects.find(x => x.data?.name === image.name);
            if (object) {
              canvasEl!.bringToFront(object);
              object.strokeWidth = strokeWidthThick;
            }
          });

          onImagesLoaded(true);
        }
      };
    });
  }, [canvasEl, fabricImages, onImagesLoaded, startImagesLoading]);

  const setAnnotations = useCallback(() => {
    if (!canvasEl || !sequenceInfo) return;

    const polygons = canvasEl.getObjects('polygon');
    canvasEl.remove(...polygons);

    fabricImages.forEach(fabricImage => {
      const image = sequenceInfo.images.find(x => getImageNameWithoutExtensions(x.filename) === getImageNameWithoutExtensions(fabricImage.name));
      if (image && image.annotations) {
        let annotationToDraw: AnnotatedFinding[] = image.annotations.map(x => ({
          isMlProposal: x.isMlProposal,
          isReviewed: x.isReviewed,
          isQaReviewed: x.isQaReviewed,
          id: x.id,
          shape: x.shape,
          homographyShape: x.homographyShape,
          dirty: false,
          disabled: x.newStatus === NewFindingStatus.AssessmentCompleted
        }));

        let fabricImageObject = canvasEl.getObjects('image').find(x => x.name === fabricImage.name);
        if (fabricImageObject) {
          loadFindings(annotationToDraw, fabricImage, fabricImageObject as fabric.Image);
        }
      }
    });
  }, [sequenceInfo, canvasEl, fabricImages, loadFindings]);

  useEffect(() => {
    setImages();
  }, [setImages]);

  useEffect(() => {
    if (!sequenceInfo || !imagesLoaded) return;

    if (canvasEl) setAnnotations();
  }, [sequenceInfo, canvasEl, imagesLoaded, setAnnotations]);
}

export default useSetImages;
